import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ticketService } from "@/services/ticketService";
import { userService } from "@/services/userService";
import { attachmentService } from "@/services/attachmentService";
import { formatError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITY_OPTIONS, CATEGORY_OPTIONS, MODULE_OPTIONS } from "@/utils/constants";
import { toast } from "sonner";
import { Paperclip, X, FileText, ArrowLeft } from "lucide-react";

export default function CreateTicketPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", category: "", module: "", assigned_to: "", due_date: "", tags: "" });
  const [stagedFiles, setStagedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => { if (user?.role !== "end_user") { userService.listAgents().then(({ data }) => setAgents(data)).catch(() => {}); } }, [user]);

  const handleAddFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} exceeds 10 MB`); return false; }
      return true;
    });
    setStagedFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index) => setStagedFiles((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) { toast.error("Title and description are required"); return; }
    if (!form.category) { toast.error("Category is required"); return; }
    if (!form.module) { toast.error("Module is required"); return; }
    if (!form.priority) { toast.error("Priority is required"); return; }
    setLoading(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [], assigned_to: form.assigned_to || null, due_date: form.due_date || null };
      const { data } = await ticketService.create(payload);

      if (stagedFiles.length > 0) {
        for (let i = 0; i < stagedFiles.length; i++) {
          setUploadProgress(Math.round(((i) / stagedFiles.length) * 100));
          try { await attachmentService.upload(data.id, stagedFiles[i]); }
          catch { toast.error(`Failed to upload ${stagedFiles[i].name}`); }
        }
        setUploadProgress(100);
      }

      toast.success(`Ticket ${data.ticket_number} created${stagedFiles.length ? ` with ${stagedFiles.length} file(s)` : ""}`);
      navigate(`/tickets/${data.id}`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setLoading(false); setUploadProgress(0); }
  };

  return (
    <div className="p-6" data-testid="create-ticket-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/tickets")} className="rounded-sm gap-1" data-testid="back-to-tickets">
            <ArrowLeft size={15} /> Back
          </Button>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#09090B]">Create New Ticket</h1>
        </div>

        <form onSubmit={handleSubmit} data-testid="create-ticket-form">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
              <TicketDetailsCard form={form} setForm={setForm} />
              <AttachmentsCard fileInputRef={fileInputRef} stagedFiles={stagedFiles} handleAddFiles={handleAddFiles} removeFile={removeFile} loading={loading} uploadProgress={uploadProgress} />
            </div>
            <div className="space-y-4">
              <PropertiesCard form={form} setForm={setForm} agents={agents} userRole={user?.role} />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-6 pt-4 border-t border-[#E4E4E7]">
            <Button type="submit" disabled={loading} data-testid="submit-ticket-btn" className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white font-semibold px-6">
              {loading ? "Creating..." : "Create Ticket"}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate("/tickets")} className="rounded-sm" data-testid="cancel-ticket-btn">Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TicketDetailsCard({ form, setForm }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-3"><CardTitle className="font-heading text-sm font-bold tracking-tight">Ticket Details</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Title *</Label>
          <Input data-testid="ticket-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Brief summary of the issue" className="rounded-sm border-zinc-300" required />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Description *</Label>
          <Textarea data-testid="ticket-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Provide a detailed description of the issue, steps to reproduce, expected behavior..." className="rounded-sm border-zinc-300 min-h-[160px]" required />
        </div>
      </CardContent>
    </Card>
  );
}

function AttachmentsCard({ fileInputRef, stagedFiles, handleAddFiles, removeFile, loading, uploadProgress }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-3"><CardTitle className="font-heading text-sm font-bold tracking-tight">Attachments</CardTitle></CardHeader>
      <CardContent>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleAddFiles} data-testid="create-file-input" />
        <div className="border-2 border-dashed border-zinc-200 rounded-sm p-4 text-center hover:border-zinc-300 transition-colors cursor-pointer" onClick={() => fileInputRef.current?.click()} data-testid="create-attach-btn">
          <Paperclip size={20} className="mx-auto text-[#52525B] mb-1.5" />
          <p className="text-sm text-[#52525B]">Click to add files</p>
          <p className="text-[10px] text-[#52525B] mt-0.5">Max 10 MB per file. PDF, images, documents, CSV, JSON</p>
        </div>
        {stagedFiles.length > 0 && (
          <div className="space-y-1 mt-3" data-testid="staged-files-list">
            {stagedFiles.map((f, i) => (
              <div key={`${f.name}-${f.size}`} className="flex items-center gap-2 px-2.5 py-2 bg-[#F4F4F5] rounded-sm text-xs" data-testid={`staged-file-${i}`}>
                <FileText size={14} className="text-[#52525B] shrink-0" />
                <span className="flex-1 truncate text-[#09090B] font-medium">{f.name}</span>
                <span className="text-[#52525B] shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="text-[#52525B] hover:text-red-500" data-testid={`remove-file-${i}`}><X size={14} /></button>
              </div>
            ))}
          </div>
        )}
        {loading && uploadProgress > 0 && <Progress value={uploadProgress} className="h-1.5 mt-2" data-testid="create-upload-progress" />}
      </CardContent>
    </Card>
  );
}

function PropertiesCard({ form, setForm, agents, userRole }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-3"><CardTitle className="font-heading text-sm font-bold tracking-tight">Properties</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Category <span className="text-red-500">*</span></Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="rounded-sm" data-testid="ticket-category-select"><SelectValue placeholder="Select category..." /></SelectTrigger>
            <SelectContent>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Module <span className="text-red-500">*</span></Label>
          <Select value={form.module} onValueChange={(v) => setForm({ ...form, module: v })}>
            <SelectTrigger className="rounded-sm" data-testid="ticket-module-select"><SelectValue placeholder="Select module..." /></SelectTrigger>
            <SelectContent>{MODULE_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Priority <span className="text-red-500">*</span></Label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="rounded-sm" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Separator />
        {userRole !== "end_user" && (
          <div className="space-y-1.5">
            <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Assign To</Label>
            <Select value={form.assigned_to || "none"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-sm" data-testid="ticket-assignee-select"><SelectValue placeholder="Select agent..." /></SelectTrigger>
              <SelectContent>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Due Date</Label>
          <Input type="date" data-testid="ticket-due-date-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm border-zinc-300" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Tags</Label>
          <Input data-testid="ticket-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g. urgent, backend" className="rounded-sm border-zinc-300" />
          <p className="text-[10px] text-[#52525B]">Comma-separated</p>
        </div>
      </CardContent>
    </Card>
  );
}
